<template>
  <paginated-table
    id="profile-loans-table"
    ref="table"
    :label="$tc('loans.model_name', 2)"
    :columns="columns"
    :extra-filters="extraFilters"
    :load-relations="['loanable.active_incidents', 'loanable.merged_user_roles.user']"
    endpoint="loans"
    :fetch-params="{ for: 'profile' }"
    sync-filters-with-url
    :show-action-column="false"
    @filters-changed="filtersUpdated"
    @row-clicked="(item) => $router.push(`/loans/${item.id}`)"
  >
    <template #cell(status)="{ item }">
      <loan-status :item="item"></loan-status>
    </template>
    <template #head-buttons>
      <b-dropdown
        right
        :text="modeText"
        variant="ghost-secondary"
        :toggle-class="{ active: modeText !== $t('views.profile.loans.filters.all_loans') }"
      >
        <b-dropdown-item
          v-if="modeText !== $t('views.profile.loans.filters.all_loans')"
          @click="showAllLoans"
        >
          {{ $t("views.profile.loans.filters.all_loans") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="modeText !== $t('views.profile.loans.filters.my_loans')"
          @click="showMyLoans"
        >
          {{ $t("views.profile.loans.filters.my_loans") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="modeText !== $t('views.profile.loans.filters.vehicle_loans')"
          @click="showLoansOfMyLoanables"
        >
          {{ $t("views.profile.loans.filters.vehicle_loans") }}
        </b-dropdown-item>
      </b-dropdown>
    </template>
  </paginated-table>
</template>

<script>
import LoanStatus from "@/components/Loan/Status/LoanStatusBadge.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import { capitalize } from "@/helpers/filters";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import Authenticated from "@/mixins/Authenticated";
import dayjs from "dayjs";

export default {
  name: "ProfileLoans",
  components: { LoanStatus, PaginatedTable },
  mixins: [Authenticated],
  data() {
    return {
      filters: {},
      extraFilters: [
        new Filter(
          "loanable.managedBy",
          this.$t("views.profile.loans.columns.managed_by"),
          "relation",
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "profile" },
          }
        ),
      ],
    };
  },
  computed: {
    columns() {
      return [
        new Column("id", capitalize(this.$t("lists.id")), "id", { showByDefault: false }),
        new Column("departure_at", capitalize(this.$t("loans.fields.departure_at")), "date", {
          formatter: (value, key, item) =>
            dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm"),
        }),
        new Column("actual_return_at", capitalize(this.$t("loans.fields.return_at")), "date", {
          showByDefault: false,
          formatter: (value, key, item) =>
            dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm"),
        }),
        Column.withRelation(
          "borrower_user.full_name",
          capitalize(this.$t("loans.fields.borrower")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "profile" },
          },
          "borrower_user.id"
        ),
        Column.withRelation(
          "loanable.owner_user.full_name",
          capitalize(this.$t("loanables.fields.owner")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "profile" },
          },
          "loanable.owner_user_id",
          { showByDefault: false, sortable: false }
        ),
        Column.withRelation(
          "community.name",
          capitalize(this.$t("loans.fields.community_id")),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read", "id") },
          },
          "community_id",
          { showByDefault: false }
        ),
        Column.withRelation(
          "loanable.name",
          capitalize(this.$t("loans.fields.loanable")),
          {
            relation: "loanables",
            label: "name",
            field: "id",
            params: { with_deleted: true },
          },
          "loanable.id",
          {
            urlFct: (item) => {
              if (item.loanable.deleted_at) {
                return null;
              }

              if (item.loanable.can_edit) {
                return `/profile/loanables/${item.loanable.id}`;
              }

              return null;
            },
          }
        ),

        Column.withSelect("status", capitalize(this.$t("loans.fields.status")), [
          { value: null, label: capitalize(this.$t("loans.statuses.null.all")) },
          { value: "requested", label: capitalize(this.$t("loans.statuses.requested.label")) },
          { value: "waiting", label: capitalize(this.$t("loans.statuses.waiting.label")) },
          { value: "accepted", label: capitalize(this.$t("loans.statuses.accepted.label")) },
          { value: "confirmed", label: capitalize(this.$t("loans.statuses.confirmed.label")) },
          { value: "ongoing", label: capitalize(this.$t("loans.statuses.ongoing.label")) },
          { value: "ended", label: capitalize(this.$t("loans.statuses.ended.label")) },
          { value: "validated", label: capitalize(this.$t("loans.statuses.validated.label")) },
          { value: "blocked", label: capitalize(this.$t("loans.statuses.blocked.label")) },
          { value: "completed", label: capitalize(this.$t("loans.statuses.completed.label")) },
          { value: "rejected", label: capitalize(this.$t("loans.statuses.rejected.label")) },
          { value: "canceled", label: capitalize(this.$t("loans.statuses.canceled.label")) },
        ]),
        new Column(
          "duration_in_minutes",
          capitalize(this.$t("loans.fields.duration_in_minutes")),
          "number",
          {
            showByDefault: false,
          }
        ),
        Column.withSelect(
          "loanable.type",
          capitalize(this.$t("loanables.fields.type")),
          [
            { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
            { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
            { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
            { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
          ],
          { showByDefault: false }
        ),

        Column.withSelect(
          "is_self_service",
          capitalize(this.$t("loans.self_service.is_self_service")),
          [
            { value: null, label: capitalize(this.$t("loans.self_service.all")) },
            { value: true, label: capitalize(this.$t("loans.self_service.is_self_service")) },
            { value: false, label: capitalize(this.$t("loans.self_service.on_demand")) },
          ],
          { showByDefault: false }
        ),
      ];
    },
    modeText() {
      if (this.filters["borrower_user.id"] === this.$store.state.user.id) {
        return this.$t("views.profile.loans.filters.my_loans");
      }

      if (this.filters["loanable.managedBy"] === this.$store.state.user.id) {
        return this.$t("views.profile.loans.filters.vehicle_loans");
      }

      return this.$t("views.profile.loans.filters.all_loans");
    },
  },
  methods: {
    filtersUpdated(filters) {
      this.filters = filters;
    },
    showMyLoans() {
      const filters = {
        ...this.filters,
        "borrower_user.id": this.$store.state.user.id,
      };

      delete filters["loanable.managedBy"];

      this.$refs.table.setFilters(filters);
    },
    showAllLoans() {
      const filters = {
        ...this.filters,
      };

      delete filters["loanable.managedBy"];
      delete filters["borrower_user.id"];

      this.$refs.table.setFilters(filters);
    },
    showLoansOfMyLoanables() {
      const filters = {
        ...this.filters,
        "loanable.managedBy": this.$store.state.user.id,
      };

      delete filters["borrower_user.id"];

      this.$refs.table.setFilters(filters);
    },
  },
};
</script>

<style lang="scss"></style>
